import { AREAS, areasDoItem, type AreaDeNovidade, type CriterioDeArea, AREA_GERAL } from '@/changelogAreas'
import type { ChangelogEntry, ChangelogItem } from '@/changelog'
import type { AuthUser } from '@/api/auth'
import { podeEmAlgumTenant, podeNaPlataforma } from '@/store/auth'

export type CanFn = (resource: string, action: string, tenantId?: number | null) => boolean

export type EntradaFiltrada = ChangelogEntry & {
  /** `false` quando algum item da versão foi filtrado: o resumo cobre a versão inteira e
   * pode falar do que a pessoa não vê (spec 2026-10-27-novidades-por-perfil §5.3). */
  resumoVisivel: boolean
}

/** Divide no ÚLTIMO ':' — 'ztna:onboard:provision' é recurso 'ztna:onboard' + ação
 * 'provision', como a Sidebar (`isVisible`) faz. */
function dividir(permissao: string): [string, string] {
  const i = permissao.lastIndexOf(':')
  return i >= 0 ? [permissao.slice(0, i), permissao.slice(i + 1)] : [permissao, '']
}

function areaVisivel(
  area: AreaDeNovidade,
  user: AuthUser | null,
  can: CanFn,
  criterios: Record<CriterioDeArea, boolean>,
  comErro: ReadonlySet<CriterioDeArea>,
): boolean {
  if ((area.requer ?? []).some((p) => podeEmAlgumTenant(user, can, ...dividir(p)))) return true
  if ((area.requerNaPlataforma ?? []).some((p) => podeNaPlataforma(user, ...dividir(p)))) return true
  // Consulta que falhou conta como visível: ruído a mais, nunca novidade sumida (§5.3).
  return area.criterio ? criterios[area.criterio] || comErro.has(area.criterio) : false
}

/** Chaves das áreas cujas novidades a pessoa recebe. `requer` vale em qualquer tenant; o critério
 * `dashboard` segue o tenant do seletor, como no menu. */
export function areasVisiveis(
  user: AuthUser | null,
  can: CanFn,
  criterios: Record<CriterioDeArea, boolean>,
  comErro: ReadonlySet<CriterioDeArea> = new Set(),
): Set<string> {
  const visiveis = new Set<string>([AREA_GERAL])
  for (const area of AREAS) {
    if (areaVisivel(area, user, can, criterios, comErro)) visiveis.add(area.chave)
  }
  return visiveis
}

const visivel = (i: ChangelogItem, visiveis: Set<string>) => areasDoItem(i).some((a) => visiveis.has(a))

/** Mantém só os itens visíveis; versão sem nenhum item visível sai. */
export function filtrarEntradas(entries: ChangelogEntry[], visiveis: Set<string>): EntradaFiltrada[] {
  const saida: EntradaFiltrada[] = []
  for (const e of entries) {
    const features = e.features.filter((i) => visivel(i, visiveis))
    const fixes = e.fixes.filter((i) => visivel(i, visiveis))
    const improvements = e.improvements.filter((i) => visivel(i, visiveis))
    const mantidos = features.length + fixes.length + improvements.length
    if (mantidos === 0) continue
    const total = e.features.length + e.fixes.length + e.improvements.length
    saida.push({ ...e, features, fixes, improvements, resumoVisivel: mantidos === total })
  }
  return saida
}
